// src/hooks/useYesterdayMetricsValidation.ts
//
// Validates whether the current user submitted at least 8 hours of work metrics
// for the previous working day. If not (and the isPluginBeta feature flag is
// enabled), conversation cards are blocked via the `conversation-disabled` CSS class.
//
// Flow:
//   1. MainLayout calls validateYesterdayMetrics() once on page load.
//   2. The function calls GET DailyAssetMetric/GetYesterdayPersonDailyAssetMetricList.
//   3. Result is stored in localStorage as C2_yesterdayHours ('true' | 'false').
//   4. A custom DOM event C2_yesterdayHours_changed is dispatched so all
//      DashboardGroupColumn instances re-evaluate without needing prop drilling.
//   5. useYesterdayMetrics() (called inside DashboardGroupColumn) reacts to that
//      event and returns the current cardsDisabled boolean.

import { useState, useEffect } from 'react';
import { apiService } from '@/services/api';

// ── Helpers ────────────────────────────────────────────────────────────────────

function isAdmin(): boolean
{
  try
  {
    return JSON.parse(sessionStorage.getItem('C2_data') || '{}')?.isAdmin === true;
  }
  catch
  {
    return false;
  }
}

/** Derives cardsDisabled from current localStorage/sessionStorage state. */
export function computeCardsDisabled(): boolean
{
  // Feature flag — if not enabled for this user, never block
  const isPluginBeta = localStorage.getItem('C2_isSecurityGroupBeta') === 'true';
  if (!isPluginBeta) return false;

  // Exempt roles: admins and PTO approvers are never blocked
  if (isAdmin()) return false;
  if (localStorage.getItem('C2_PTOApprover') === 'true') return false;

  // 'false' means yesterday's hours were < 8 → block cards
  return localStorage.getItem('C2_yesterdayHours') === 'false';
}

// ── Exported async function (called from MainLayout on page load) ──────────────

/**
 * Fetches yesterday's daily asset metrics for the current user and stores the
 * result in localStorage so card components can read it synchronously.
 *
 * Blocking conditions (all must be true):
 *   - isPluginBeta flag is enabled (C2_isSecurityGroupBeta === 'true')
 *   - User is NOT admin
 *   - User is NOT PTO approver
 *   - Yesterday's total actual hours < 8
 *   - User has at least one past record (hasPastRecords === true)
 */
export async function validateYesterdayMetrics(): Promise<void>
{
  try
  {
    // 'en-CA' locale produces YYYY-MM-DD format, which the API expects
    const today = new Date().toLocaleDateString('en-CA');

    const data: any = await apiService.get(
      `DailyAssetMetric/GetYesterdayPersonDailyAssetMetricList?date=${today}`,
    );

    const metrics: any[] = data?.MetricList ?? data?.metricList ?? [];
    const hasPastRecords: boolean = data?.HasPastRecords ?? data?.hasPastRecords ?? false;

    const totalActualHours: number = metrics.reduce(
      (sum: number, m: any) => sum + (m.ActualHours ?? m.actualHours ?? 0),
      0,
    );

    // Default to true (not blocked) so new users without any records are unaffected
    let recordFromYesterday = true;

    if (totalActualHours < 8)
    {
      recordFromYesterday = false;
    }

    // First-time users have no past records — never block them
    if (!hasPastRecords)
    {
      recordFromYesterday = true;
    }

    localStorage.setItem('C2_yesterdayHours', String(recordFromYesterday));
    window.dispatchEvent(
      new CustomEvent('C2_yesterdayHours_changed', { detail: recordFromYesterday }),
    );
  }
  catch
  {
    // On error, default to unblocked so the UI remains usable
    localStorage.setItem('C2_yesterdayHours', 'true');
    window.dispatchEvent(new CustomEvent('C2_yesterdayHours_changed', { detail: true }));
  }
}

// ── Exported hook (called inside DashboardGroupColumn) ─────────────────────────

/**
 * Reactively returns whether cards should be disabled based on yesterday's
 * metrics validation. Listens for the C2_yesterdayHours_changed event so it
 * re-evaluates as soon as validateYesterdayMetrics() completes.
 */
export function useYesterdayMetrics(): boolean
{
  const [cardsDisabled, setCardsDisabled] = useState<boolean>(computeCardsDisabled);

  useEffect(() =>
  {
    const handler = () => setCardsDisabled(computeCardsDisabled());
    window.addEventListener('C2_yesterdayHours_changed', handler);
    return () => window.removeEventListener('C2_yesterdayHours_changed', handler);
  }, []);

  return cardsDisabled;
}
